import {Component, HostListener, OnInit} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {FileData, FileErrors} from "../../model/import-csv/file-data";
import {AuthGuard} from "../../common/auth-guard.service";
import {TenantsService} from "../../services/tenants.service";
import {CommonMessagesService} from "../../services/common-messages.service";
import {CsvErrorList} from "../../model/import-csv/csv-error";
import {ActivatedRoute, Router} from "@angular/router";
import {CsvMeasurementData, ImportCsvEntityType} from "../../model/import-csv/csv-measurement-data";
import {MeasurementPersistRequestCsv} from "../../model/import-csv/measurement-persist-request-csv";
import {MeasurementCsvErrors} from "./measurement-csv-errors";
import {MeasurementPropertiesErrors} from "./measurement-properties-errors";
import * as moment from "moment";
import {MeasuresService} from "../../services/measures.service";

@Component({
  selector: 'app-import-measurements-csv',
  templateUrl: './import-measurements-csv.component.html',
  styleUrls: ['./import-measurements-csv.component.scss']
})
export class ImportMeasurementsCsvComponent implements OnInit {

  public measurementPropertiesErrors: MeasurementPropertiesErrors = new MeasurementPropertiesErrors(this.i18n);

  public fileFormatIncorrect: boolean = false;
  public infoCollapsed: boolean = true;
  public files: FileData[] = [];
  public filesWithoutErrors: File[] = [];
  public measurements: CsvMeasurementData[] = [];

  public entityType: ImportCsvEntityType;
  public uuid: string;

  public loadedData: boolean = false;
  public filesLoaded: boolean = false;
  public filesUploaded: boolean = false;

  public uploadingMeasurements: boolean = false;

  public rows = 10;
  public first = 0;
  public totalMeasurements: number;

  constructor(
    private readonly commonMessageService: CommonMessagesService,
    private readonly measuresService: MeasuresService,
    private readonly i18n: TranslocoService,
    private tenantsService: TenantsService,
    private authGuard: AuthGuard,
    private route: ActivatedRoute,
    private router: Router
  ) {
  }

  ngOnInit(): void {
    let urlSegments = this.router.url.split("/");
    if (urlSegments[1] === 'devices') {
      this.entityType = ImportCsvEntityType.DEVICE;
    } else {
      this.entityType = ImportCsvEntityType.ASSET;
    }
    this.uuid = urlSegments[2];
    this.loadData();
  }

  private loadData(): void {
    this.loadedData = true;
  }

  public async processFiles(files: File[]): Promise<void> {
    const filePromises: Promise<FileData>[] = files.map((file, index) => {
      const fileIdentifier: string = 'id-' + index + '-' + (new Date()).getTime();
      return this.readFile(fileIdentifier, file)
    });

    const _files: FileData[] = await Promise.all(filePromises);

    this.files = this.files.concat(_files);
    this.filesWithoutErrors = this.files
      .filter(file => file.fileErrors == null)
      .map(file => file.file);
    this.filesLoaded = true;
    this.filesUploaded = false;
    this.infoCollapsed = true;
  }

  private readFile(fileIdentifier: string, file: File): Promise<FileData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const fileReader = reader.result.toString().split('\n');
          const fileErrors = this.processMeasurementFiles(fileIdentifier, fileReader);
          const fileData: FileData = {
            fileIdentifier,
            file,
            fileErrors
          }
          resolve(fileData);
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsText(file, 'ISO-8859-3');
      reader.onerror = (error) => {
        reject(error)
      };
    });
  }

  public deleteAttachment(index: number): void {
    const fileIdentifier = this.files[index]['fileIdentifier'];
    this.files.splice(index, 1);
    if (this.files.length == 0) {
      this.filesLoaded = false;
      this.infoCollapsed = false;
    }
    this.measurements = this.measurements.filter(measurement => measurement.fileIdentifier != fileIdentifier);
    this.totalMeasurements = this.measurements.length;
    this.filesWithoutErrors = this.files
      .filter(file => file.fileErrors == null)
      .map(file => file.file);
  }

  private processMeasurementFiles(fileIdentifier: string, file: string[]): FileErrors | null {
    let measurements: CsvMeasurementData[] = [];
    let fileErrors: FileErrors = {
      lines: [],
    };

    file.forEach((line, lineIndex) => {
      if (line.trim() !== '' && !line.includes('Timestamp')) {
        const rowData = line.split(';');
        if (this.validateNumberOfParams(rowData)) {
          const measurement = this.getMeasurementPersistRequestCsv(rowData);
          let csvMeasurementData: CsvMeasurementData = {
            measurement,
            fileIdentifier,
            status: 'NONE',
            lineIdentifier: fileIdentifier + measurement.name + measurement.timestamp
          }

          const errors = this.validateMeasurementData(csvMeasurementData);
          if (errors != null) {
            csvMeasurementData.status = 'ERROR';
            csvMeasurementData.errors = errors;
            fileErrors.lines.push(lineIndex - 1);

          } else {
            csvMeasurementData.status = 'CORRECT';
          }

          measurements.push(csvMeasurementData);
        } else {
          fileErrors.lines.push(lineIndex - 1);
        }
      }
    });
    this.fileFormatIncorrect = false;

    if (measurements.length > 0) {
      this.measurements = this.measurements.concat(measurements);
    }
    this.totalMeasurements = this.measurements.length;
    return fileErrors.lines.length > 0 ? fileErrors : null;
  }

  private validateNumberOfParams(rowData: string[]): boolean {
    return rowData.length >= 3;
  }

  private getMeasurementPersistRequestCsv(rowData: string[]): MeasurementPersistRequestCsv {
    const [name, timestamp, value, unit] = rowData;

    return {
      name,
      timestamp,
      value,
      unit
    }
  }

  public isValidDate(date: string) {
    const nameRegexp: RegExp = /^[0-9]+$/;
    return !(date == null || !nameRegexp.test(date) || !moment(Number.parseInt(date)).isValid());
  }

  public parseToNumber(date: string) {
    return Number.parseInt(date);
  }

  private validateMeasurementData(csvMeasurementData: CsvMeasurementData): CsvErrorList | null {
    const measurement = csvMeasurementData.measurement;
    const csvMeasurementErrors = new MeasurementCsvErrors(this.i18n);

    csvMeasurementErrors.required(measurement, 'name');
    csvMeasurementErrors.noSpecialCharacters(measurement, 'name');
    csvMeasurementErrors.maxLength(measurement, 'name', 255);

    let present = csvMeasurementErrors.required(measurement, 'timestamp');
    if (present) {
      csvMeasurementErrors.validDate(measurement, 'timestamp');
    }

    csvMeasurementErrors.required(measurement, 'value');

    return Object.entries(csvMeasurementErrors.errors).length > 0 ? csvMeasurementErrors.errors : null;
  }

  confirmImport(): void {
    if (this.files.length > this.filesWithoutErrors.length) {
      this.commonMessageService.showImportCsvConfirmation(() => this.importCsvFiles());
    } else {
      this.importCsvFiles();
    }
  }

  public importCsvFiles(): void {
    this.measuresService
      .importMeasurementsFromCsv(this.filesWithoutErrors, this.uuid, this.entityType)
      .subscribe();
    this.commonMessageService.showSuccessfullyUploadedMessage();
    this.filesUploaded = true;
    this.files = [];
    this.filesWithoutErrors = [];
    this.measurements = [];
  }

  public cancel(): void {
    this.router.navigate([`/${this.entityType}/${this.uuid}/details`], {state: {showView: 'measures'}});
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return !this.files || this.files.length === 0;
  }
}
